import { useEffect, useMemo } from 'react'
import { indexedDBs } from '../../Config/Storage/storage.config'
import { ensureSpace, estimatedSizeBytes } from './storageHelpers'

export type CachedItem = {
  key: string
  value: any
}

type UpgradeFn = (
  db: IDBDatabase,
  oldVersion: number,
  newVersion: number | null,
  tx: IDBTransaction | null
) => void

export type IndexedDBOptions = {
  dbName?: string
  storeName?: string
  dbVersion?: number
  upgrade?: UpgradeFn
  ttlMs?: number
}

export type IndexedDBAPI<T = unknown> = {
  set(key: string, value: T): Promise<void>
  get(key: string): Promise<T | undefined>
  del(key: string): Promise<void>
  clear(): Promise<void>
  keys(): Promise<string[]>
  setMany(items: Array<{ key: string; value: T }>): Promise<void>
  getMany(keys: string[]): Promise<(T | undefined)[]>
  delMany(keys: string[]): Promise<void>
  destroy(): void
}

export const createIndexedDB = <T = unknown>(
  opts: IndexedDBOptions = {}
): IndexedDBAPI<T> => {
  const dbName = opts.dbName ?? indexedDBs.main.dbName
  const storeName = opts.storeName ?? indexedDBs.main.storeName
  const dbVersion = opts.dbVersion ?? indexedDBs.main.dbVersion
  const upgrade = opts.upgrade
  const TTL = opts.ttlMs ?? indexedDBs.main.ttlMs

  let conn: { db: IDBDatabase; lastUsed: number } | null = null
  let openPromise: Promise<IDBDatabase> | null = null
  let timer: ReturnType<typeof setInterval> | null = null
  let cleanupStarted = false
  let unloadBound = false
  let writeQueue: Promise<void> = Promise.resolve()

  const markUsed = () => {
    if (conn) conn.lastUsed = Date.now()
  }

  const startCleanup = () => {
    if (cleanupStarted || timer) return
    cleanupStarted = true
    timer = setInterval(() => {
      if (!conn) return
      if (Date.now() - conn.lastUsed > TTL) {
        try {
          conn.db.close()
        } catch {}
        conn = null
      }
    }, Math.max(60_000, Math.floor(TTL / 2))) // Min 1 minute interval
    if (typeof window !== 'undefined' && !unloadBound) {
      unloadBound = true
      window.addEventListener('beforeunload', destroy, { once: true })
    }
  }

  const destroy = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    if (unloadBound && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', destroy)
      unloadBound = false
    }
    openPromise = null
    if (conn) {
      try {
        conn.db.close()
      } catch {}
      conn = null
    }
    cleanupStarted = false
  }

  const openDB = (): Promise<IDBDatabase> => {
    if (openPromise) return openPromise
    openPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !('indexedDB' in window)) {
        openPromise = null
        reject(new Error('IndexedDB is not available'))
        return
      }
      let req: IDBOpenDBRequest
      try {
        req = indexedDB.open(dbName, dbVersion)
      } catch (e) {
        openPromise = null
        reject(new Error(`open failed: ${(e as Error).message}`))
        return
      }

      req.onerror = () => {
        openPromise = null
        reject(new Error(req.error?.message ?? 'open error'))
      }
      req.onblocked = () => {
        openPromise = null
        reject(new Error('Upgrade blocked by another tab'))
      }
      req.onupgradeneeded = (ev) => {
        const db = req.result
        if (!db.objectStoreNames.contains(storeName)) db.createObjectStore(storeName)
        try {
          upgrade?.(
            db,
            (ev as IDBVersionChangeEvent).oldVersion,
            (ev as IDBVersionChangeEvent).newVersion ?? null,
            req.transaction
          )
        } catch (e) {
          openPromise = null
          reject(new Error(`migration failed: ${(e as Error).message}`))
        }
      }
      req.onsuccess = () => {
        openPromise = null
        const db = req.result
        db.onversionchange = () => {
          try {
            db.close()
          } catch {}
        }
        resolve(db)
      }
    })
    return openPromise
  }

  const getDB = async () => {
    if (conn?.db) return conn.db
    const db = await openDB()
    conn = { db, lastUsed: Date.now() }
    openPromise = null
    startCleanup()
    return db
  }

  const txDone = (tx: IDBTransaction) =>
    new Promise<void>((res, rej) => {
      tx.oncomplete = () => res()
      tx.onabort = () => rej(tx.error ?? new Error('tx aborted'))
      tx.onerror = () => rej(tx.error ?? new Error('tx error'))
    })

  const request = <R>(req: IDBRequest<R>): Promise<R> =>
    new Promise<R>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error ?? new Error('request error'))
    })

  const withStore = async <R>(
    mode: IDBTransactionMode,
    use: (s: IDBObjectStore) => Promise<R> | R
  ): Promise<R> => {
    const db = await getDB()
    let tx: IDBTransaction
    try {
      tx = db.transaction(storeName, mode)
    } catch {
      // reopen once if closed between awaits
      destroy()
      tx = (await getDB()).transaction(storeName, mode)
    }
    const done = txDone(tx)
    const store = tx.objectStore(storeName)
    markUsed()
    try {
      const out = await use(store)
      await done
      markUsed()
      return out
    } catch (e) {
      try {
        tx.abort()
      } catch {}
      throw e
    }
  }

  const enqueueWrite = (fn: () => Promise<void>): Promise<void> => {
    // Always run after the current queue
    const run = writeQueue.then(fn, fn)
    writeQueue = run.catch((err) => {
      console.error('[IDB] write failed; queue recovered:', err)
    })
    return run
  }

  return {
    async set(key: string, value: T) {
      await enqueueWrite(async () => {
        const totalBytes = [{ key, value }].reduce((sum, item) => {
          return sum + estimatedSizeBytes(item.key) + estimatedSizeBytes(item.value)
        }, 0)
        const spaceAvailable = await ensureSpace(totalBytes)
        if (!spaceAvailable) {
          console.warn('[IDB] insufficient space to write')
          return
        }
        await withStore('readwrite', async (s) => {
          await request(s.put(value, key))
        })
      })
    },
    async get(key) {
      return withStore('readonly', (s) => request<T | undefined>(s.get(key)))
    },
    async del(key: string) {
      await enqueueWrite(async () => {
        await withStore('readwrite', async (s) => {
          await request(s.delete(key))
        })
      })
    },
    async clear() {
      await enqueueWrite(async () => {
        await withStore('readwrite', async (s) => {
          await request(s.clear())
        })
      })
    },
    async setMany(items) {
      await enqueueWrite(async () => {
        const totalBytes = items.reduce((sum, { key, value }) => {
          return sum + estimatedSizeBytes(value) + estimatedSizeBytes(key)
        }, 0)
        const spaceAvailable = await ensureSpace(totalBytes)
        if (!spaceAvailable) {
          console.warn('[IDB] insufficient space to write')
          return
        }
        await withStore('readwrite', async (s) => {
          for (const { key, value } of items) await request(s.put(value, key))
        })
      })
    },
    async getMany(keys) {
      return withStore('readonly', async (s) => {
        const out: (T | undefined)[] = new Array(keys.length)
        for (let i = 0; i < keys.length; i++) {
          out[i] = await request<T | undefined>(s.get(keys[i]))
        }
        return out
      })
    },
    async delMany(keys) {
      await enqueueWrite(async () => {
        await withStore('readwrite', async (s) => {
          for (const k of keys) await request(s.delete(k))
        })
      })
    },
    async keys(): Promise<string[]> {
      return await withStore('readonly', async (store) => {
        const s = store as unknown as { getAllKeys?: () => IDBRequest<IDBValidKey[]> }
        if (typeof s.getAllKeys === 'function') {
          return await request(s.getAllKeys()).then((ks) => ks.map(String))
        }
        return new Promise<string[]>((resolve, reject) => {
          const out: string[] = []
          const req = store.openKeyCursor()
          req.onsuccess = (ev) => {
            const cursor = (ev.target as IDBRequest<IDBCursor | null>).result
            if (cursor) {
              out.push(String(cursor.key))
              cursor.continue()
            } else {
              resolve(out)
            }
          }
          req.onerror = () => reject(req.error ?? new Error('cursor error'))
        })
      })
    },
    destroy,
  }
}

export const useIndexedDB = <T = unknown>(opts?: {
  dbName?: string
  storeName?: string
  dbVersion?: number
  upgrade?: UpgradeFn
  ttlMs?: number
}): IndexedDBAPI<T> => {
  const api = useMemo(
    () => createIndexedDB<T>(opts),
    [opts?.dbName, opts?.storeName, opts?.dbVersion, opts?.ttlMs, opts?.upgrade]
  )
  useEffect(() => () => api.destroy(), [api])
  return api
}
